<script setup lang="ts">
import { computed } from 'vue';
import type { PropType } from 'vue';

import { V1SnapshotOutreachRadius, V1SnapshotScoreThreshold } from '@factoryfixinc/ats-interfaces';

import MultipleSelectionItems from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/MultipleSelectionItems/MultipleSelectionItems.vue';
import SuggestionTextArea from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/SuggestionTextArea/SuggestionTextArea.vue';
import SourcingRadiusSelector from '@/components/Jobs/CopilotActivation/SourcingRadiusSelector.vue';
import { DEFAULT_OUTREACH_RADIUS_MILES } from '@/core/jobs/copilot-activation/outreach-radius.constants';
import type { CopilotConfigurationFormData } from '@/components/CopilotPersonalities/types/copilot-configuration-template.interface';
import { isEnumMember } from '@/utils/is-enum-member.util';

// Props
// ===============================
const props = defineProps({
  modelValue: {
    type: Object as PropType<CopilotConfigurationFormData>,
  },
});

// Emits
// ===============================
const emit = defineEmits<{
  (e: 'update:modelValue', payload: CopilotConfigurationFormData): void;
}>();

// Computed properties
// ===============================
const activeOutreachEnabledItems = computed(() => {
  const isPassiveSelected = !!props.modelValue?.activeOutreachEnabled;
  return [
    {
      title: 'Active job seekers + passive candidates',
      subtitle: 'Recommended for maximum reach',
      icon: 'stack' as const,
      isSelected: isPassiveSelected,
      // Render the Sourcing radius selector inline inside this card via the
      // selected-callout slot — only when this option is the selected one.
      showCallout: isPassiveSelected,
      tooltipText: '',
      value: 'true',
    },
    {
      title: 'Active job seekers only',
      subtitle: 'Focus on candidates actively looking for jobs',
      icon: 'single-stack-item' as const,
      isSelected: !isPassiveSelected,
      tooltipText: '',
      value: 'false',
    },
  ];
});

const scoreThresholdItems = computed(() => {
  return [
    {
      title: 'Maximize your talent pipeline',
      subtitle: 'Copilot will contact promising candidates, even when their fit is less certain',
      icon: 'three-star' as const,
      isSelected: props.modelValue?.scoreThreshold === V1SnapshotScoreThreshold.THREE_PLUS,
      tooltipText: '',
      value: `${V1SnapshotScoreThreshold.THREE_PLUS}`,
    },
    {
      title: 'Focus on top-tier matches only',
      subtitle: 'Copilot will only contact candidates with high likelihood of fit',
      icon: 'four-star' as const,
      isSelected: props.modelValue?.scoreThreshold === V1SnapshotScoreThreshold.FOUR_PLUS,
      tooltipText: '',
      value: `${V1SnapshotScoreThreshold.FOUR_PLUS}`,
    },
  ];
});

const scheduledUpdatesItems = computed(() => {
  return [
    {
      title: 'Optimize visibility',
      subtitle:
        'Refresh and enhance job postings - stay on the top of searches, boost applicant flow',
      icon: 'refresh' as const,
      isSelected: !!props.modelValue?.scheduledUpdates,
      tooltipText: '',
      value: 'true',
    },
    {
      title: 'Keep original',
      subtitle:
        'Do not refresh the job postings - maintain exact job descriptions written by your team',
      icon: 'refresh-locked' as const,
      isSelected: !props.modelValue?.scheduledUpdates,
      tooltipText: '',
      value: 'false',
    },
  ];
});

const activeOutreachEnabled = computed({
  get: () => Boolean(props.modelValue?.activeOutreachEnabled ?? true),
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      activeOutreachEnabled: value,
    } as CopilotConfigurationFormData);
  },
});

function setScoreThreshold(value: unknown) {
  if (!isEnumMember(V1SnapshotScoreThreshold, value)) {
    return;
  }

  emit('update:modelValue', {
    ...props.modelValue,
    scoreThreshold: value,
  } as CopilotConfigurationFormData);
}

const scheduledUpdates = computed({
  get: () => Boolean(props.modelValue?.scheduledUpdates ?? true),
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      scheduledUpdates: value,
    } as CopilotConfigurationFormData);
  },
});

const targetCompanies = computed({
  get: () => props.modelValue?.targetCompanies || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      targetCompanies: value,
    } as CopilotConfigurationFormData);
  },
});

const avoidCompanies = computed({
  get: () => props.modelValue?.avoidCompanies || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      avoidCompanies: value,
    } as CopilotConfigurationFormData);
  },
});

const perksAndPrograms = computed({
  get: () => props.modelValue?.perksAndPrograms || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      perksAndPrograms: value,
    } as CopilotConfigurationFormData);
  },
});

const sourcingRadius = computed<V1SnapshotOutreachRadius>({
  get: () => props.modelValue?.outreachRadiusMiles ?? DEFAULT_OUTREACH_RADIUS_MILES,
  set: (value) => {
    const next: CopilotConfigurationFormData = {
      ...(props.modelValue as CopilotConfigurationFormData),
    };
    if (value === DEFAULT_OUTREACH_RADIUS_MILES) {
      delete next.outreachRadiusMiles;
    } else {
      next.outreachRadiusMiles = value;
    }
    emit('update:modelValue', next);
  },
});
</script>

<template>
  <section>
    <!-- Active Outreach (Sourcing radius nests inside the selected "+ passive" card) -->
    <MultipleSelectionItems
      title="Candidate sourcing"
      title-class="!text-base !font-extrabold"
      subtitle="Who should your recruiter target?"
      :items="activeOutreachEnabledItems"
      :selected-callout-full-width="true"
      class="mb-8"
      @click:select="activeOutreachEnabled = $event === 'true'"
    >
      <template #selected-callout="{ item }">
        <SourcingRadiusSelector
          v-if="item.value === 'true'"
          v-model="sourcingRadius"
          :embedded="true"
        />
      </template>
    </MultipleSelectionItems>

    <!-- Score Threshold -->
    <MultipleSelectionItems
      title="Passive candidate approach"
      title-class="!text-base !font-extrabold"
      subtitle="How selective should your recruiter be when reaching out to passive candidates?"
      :items="scoreThresholdItems"
      class="mb-8"
      @click:select="setScoreThreshold"
    />

    <!-- Scheduled Updates -->
    <MultipleSelectionItems
      title="Job visibility"
      title-class="!text-base !font-extrabold"
      subtitle="How should your recruiter manage job visibility on external job boards?"
      :items="scheduledUpdatesItems"
      class="mb-8"
      @click:select="scheduledUpdates = $event === 'true'"
    />

    <!-- Target Companies -->
    <SuggestionTextArea
      v-model="targetCompanies"
      title="Which companies employ candidates with backgrounds and skills strongly aligned to your needs?"
      title-class="!text-base !font-extrabold"
      :placeholder="`e.g., Competitors or local companies with strong, relevant talent`"
      optional
    />

    <!-- Avoid Companies -->
    <SuggestionTextArea
      v-model="avoidCompanies"
      title="Which companies' employees should your recruiter avoid when sourcing candidates?"
      title-class="!text-base !font-extrabold"
      :placeholder="`e.g., Partner companies, companies that produce weak candidates, etc.`"
      optional
    />

    <!-- Perks and Programs -->
    <SuggestionTextArea
      v-model="perksAndPrograms"
      :title="`What perks and programs make your organization attractive to potential candidates and how would your best recruiter sell it?`"
      title-class="!text-base !font-extrabold"
      :placeholder="`e.g., Tuition reimbursement, flexible scheduling, comprehensive benefits package…`"
      optional
    />
  </section>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-combobox__selection {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-input :deep(.v-field.v-field--error),
.v-input--horizontal :deep(.v-field.v-field--error) {
  .v-field__outline {
    @apply text-critical-500;
  }
}
</style>
